import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { isViewer } from "../../../shared/utils/roles";
import { selectCurrentUser } from "../../auth/store/authSlice";
import {
  getGreetingFileById,
  getGreetingUploadUrlById,
  uploadGreetingFileToPresignedUrl,
} from "../services/numbersService";
import {
  buyNumberThunk,
  clearNumbersError,
  fetchPurchasedNumbersThunk,
  releaseNumberThunk,
  resetSearchForm,
  searchAvailableNumbersThunk,
  selectNumbersState,
  setMatchTo,
  setSearchCriteria,
  setSearchDigits,
  setSelectedCountry,
  toggleAvailableSpread,
  togglePurchasedSpread,
  updateGreetingMetadata,
} from "../store/numbersSlice";

export const useNumberManagement = () => {
  const dispatch = useDispatch();
  const currentUser = useSelector(selectCurrentUser);
  const numbersState = useSelector(selectNumbersState);

  const [showBuyModal, setShowBuyModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showReleaseModal, setShowReleaseModal] = useState(false);
  const [selectedNumber, setSelectedNumber] = useState(null);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const resolvedClientId = useMemo(() => {
    return Number(currentUser?.client_id || 0) || null;
  }, [currentUser?.client_id]);

  // RBAC: Determine if user can edit numbers (not viewer)
  const canEditNumbers = useMemo(() => {
    return !isViewer(currentUser?.user_role);
  }, [currentUser?.user_role]);

  const buildGreetingFileName = useCallback((phoneNumber) => {
    const digits = String(phoneNumber || "").replace(/\D/g, "");
    const normalizedDigits =
      digits.length === 11 && digits.startsWith("1") ? digits.slice(1) : digits;

    return normalizedDigits ? `${normalizedDigits}.mp3` : "";
  }, []);

  const showMessage = useCallback((message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  }, []);

  useEffect(() => {
    if (!resolvedClientId) {
      return;
    }
    dispatch(fetchPurchasedNumbersThunk(resolvedClientId));
  }, [dispatch, resolvedClientId]);

  useEffect(() => {
    if (!numbersState.error) {
      return;
    }

    showMessage(numbersState.error, "error");
    dispatch(clearNumbersError());
  }, [dispatch, numbersState.error, showMessage]);

  const handleSearch = useCallback(async () => {
    await dispatch(searchAvailableNumbersThunk());
  }, [dispatch]);

  const handleReset = useCallback(() => {
    dispatch(resetSearchForm());
  }, [dispatch]);

  const handleBuyClick = useCallback((number) => {
    if (!canEditNumbers) {
      showMessage("Viewers can only search and view numbers.", "error");
      return;
    }
    setSelectedNumber(number);
    setShowBuyModal(true);
  }, [canEditNumbers, showMessage]);

  const handleBuyConfirm = useCallback(
    async (number) => {
      const action = await dispatch(
        buyNumberThunk({ number, clientId: resolvedClientId }),
      );

      if (buyNumberThunk.fulfilled.match(action)) {
        showMessage("Phone number purchased successfully.");
        setShowBuyModal(false);
        setSelectedNumber(null);
      }
    },
    [dispatch, resolvedClientId, showMessage],
  );

  const handleUploadClick = useCallback((number) => {
    if (!canEditNumbers) {
      showMessage("Viewers can only search and view numbers.", "error");
      return;
    }
    setSelectedNumber(number);
    setShowUploadModal(true);
  }, [canEditNumbers, showMessage]);

  const handleUploadGreeting = useCallback(
    async (number, file) => {
      if (!number?.id) {
        showMessage("Phone number id is missing.", "error");
        return;
      }

      const fileName = file?.name || "";
      const lowerName = fileName.toLowerCase();
      if (!lowerName.endsWith(".mp3")) {
        showMessage("Only .mp3 files are allowed.", "error");
        return;
      }

      const targetFileName = buildGreetingFileName(number.phoneNumber);
      if (!targetFileName) {
        showMessage("Phone number is missing. Unable to name greeting file.", "error");
        return;
      }

      const fileToUpload =
        file?.name === targetFileName
          ? file
          : new File([file], targetFileName, { type: "audio/mpeg" });

      const resolvedContentType = "audio/mpeg";

      if (!resolvedClientId) {
        showMessage("Client id is missing. Please refresh and try again.", "error");
        return;
      }

      const uploadResponse = await getGreetingUploadUrlById(number.id, {
        client_id: resolvedClientId,
      });

      if (uploadResponse?.error) {
        showMessage(
          uploadResponse.error.message || "Failed to request upload URL.",
          "error",
        );
        return;
      }

      const uploadUrl =
        uploadResponse?.greetings_upload_url ||
        uploadResponse?.greeting_upload_url ||
        uploadResponse?.presigned_url ||
        uploadResponse?.presignedUrl ||
        uploadResponse?.upload_url ||
        uploadResponse?.uploadUrl ||
        uploadResponse?.data?.greetings_upload_url ||
        uploadResponse?.data?.greeting_upload_url ||
        uploadResponse?.data?.presigned_url ||
        uploadResponse?.data?.presignedUrl ||
        uploadResponse?.data?.upload_url ||
        uploadResponse?.data?.uploadUrl ||
        "";

      const uploadHeaders =
        uploadResponse?.headers ||
        uploadResponse?.data?.headers ||
        {};

      let signedHeaders = "";
      try {
        signedHeaders = new URL(uploadUrl).searchParams.get("X-Amz-SignedHeaders") || "";
      } catch (error) {
        signedHeaders = "";
      }

      const signedContentType =
        uploadResponse?.content_type ||
        uploadResponse?.contentType ||
        uploadResponse?.data?.content_type ||
        uploadResponse?.data?.contentType ||
        (() => {
          const lowerUrl = String(uploadUrl).toLowerCase();
          if (lowerUrl.includes(".mp3")) return "audio/mpeg";
          return resolvedContentType || "application/octet-stream";
        })();

      if (!uploadUrl) {
        showMessage("Upload URL was not returned by the server.", "error");
        return;
      }

      const uploadKey = String(uploadUrl).split("?")[0].split("/").pop();
      if (targetFileName && uploadKey && uploadKey !== targetFileName) {
        showMessage(
          `Backend returned an upload URL for "${uploadKey}" instead of "${targetFileName}". Please ask backend to sign the correct file name.`,
          "error",
        );
        return;
      }

      let uploadResult;
      try {
        const shouldSendContentType = signedHeaders.includes("content-type");
        uploadResult = await uploadGreetingFileToPresignedUrl(
          uploadUrl,
          fileToUpload,
          {
            ...uploadHeaders,
            ...(shouldSendContentType && signedContentType
              ? { "Content-Type": signedContentType }
              : {}),
          },
        );
      } catch (error) {
        const rawMessage = error?.message || "Failed to upload greeting file.";
        const isCorsError = rawMessage.toLowerCase().includes("failed to fetch");
        showMessage(
          isCorsError
            ? "Upload blocked by CORS. Ask backend to allow PUT from this origin in the greetings S3 bucket."
            : rawMessage,
          "error",
        );
        return;
      }

      if (!uploadResult?.ok) {
        let errorDetails = "";
        try {
          errorDetails = await uploadResult.text();
        } catch (error) {
          errorDetails = "";
        }

        const isSignatureMismatch = errorDetails.includes("SignatureDoesNotMatch");

        showMessage(
          isSignatureMismatch
            ? "Upload failed: signature mismatch. Backend must sign the presigned URL with the same file name and content-type you upload."
            : errorDetails
              ? `Upload failed: ${errorDetails}`
              : "Failed to upload greeting file.",
          "error",
        );
        return;
      }

      dispatch(
        updateGreetingMetadata({
          numberId: number.id,
          greetingFile: targetFileName,
          greetingUrl: "",
        }),
      );

      dispatch(fetchPurchasedNumbersThunk(resolvedClientId));

      showMessage("Greeting file uploaded successfully. Click to play.");
      setShowUploadModal(false);
      setSelectedNumber(null);
    },
    [buildGreetingFileName, dispatch, resolvedClientId, showMessage],
  );

  const handlePlayGreeting = useCallback(
    async (number) => {
      if (!number?.id) {
        return;
      }

      if (number.greetingUrl) {
        window.open(number.greetingUrl, "_blank", "noopener,noreferrer");
        return;
      }

      const response = await getGreetingFileById(number.id);
      if (response?.error) {
        showMessage("Greeting file is not available yet.", "error");
        return;
      }

      const data = response?.data || response;
      const greetingUrl =
        data?.greetings_url || data?.greeting_url || data?.greetingsUrl || data?.greetingUrl || "";

      if (!greetingUrl) {
        showMessage("Greeting file is not available yet.", "error");
        return;
      }

      const fileName = String(greetingUrl).split("?")[0].split("/").pop();
      dispatch(
        updateGreetingMetadata({
          numberId: number.id,
          greetingUrl,
          greetingFile: fileName || number.greetingFile,
        }),
      );

      window.open(greetingUrl, "_blank", "noopener,noreferrer");
    },
    [dispatch, showMessage],
  );

  const handleReleaseClick = useCallback((number) => {
    if (!canEditNumbers) {
      showMessage("Viewers can only search and view numbers.", "error");
      return;
    }
    setSelectedNumber(number);
    setShowReleaseModal(true);
  }, [canEditNumbers, showMessage]);

  const handleReleaseConfirm = useCallback(
    async (number) => {
      const action = await dispatch(
        releaseNumberThunk({ number, clientId: resolvedClientId }),
      );

      if (releaseNumberThunk.fulfilled.match(action)) {
        showMessage("Phone number deleted successfully.");
        setShowReleaseModal(false);
        setSelectedNumber(null);
      }
    },
    [dispatch, resolvedClientId, showMessage],
  );

  const handleCancel = useCallback(() => {
    setShowBuyModal(false);
    setShowUploadModal(false);
    setShowReleaseModal(false);
    setSelectedNumber(null);
  }, []);

  return {
    ...numbersState,
    setSelectedCountry: (value) => dispatch(setSelectedCountry(value)),
    setSearchCriteria: (value) => dispatch(setSearchCriteria(value)),
    setSearchDigits: (value) => dispatch(setSearchDigits(value)),
    setMatchTo: (value) => dispatch(setMatchTo(value)),
    onToggleAvailableSpread: () => dispatch(toggleAvailableSpread()),
    onTogglePurchasedSpread: () => dispatch(togglePurchasedSpread()),
    handleSearch,
    handleReset,
    showBuyModal,
    showUploadModal,
    showReleaseModal,
    selectedNumber,
    handleBuyClick,
    handleBuyConfirm,
    handleUploadClick,
    handleUploadGreeting,
    handlePlayGreeting,
    handleReleaseClick,
    handleReleaseConfirm,
    handleCancel,
    snackbar,
    setSnackbar,
    canEditNumbers,
  };
};

export default useNumberManagement;
